import './AreaAndBarChart.scss';
import { humanize } from 'inflected';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Area, Bar, ComposedChart, Tooltip } from 'recharts';
import { ChartXAxis, ChartYAxis, ChartGrid } from '@Components/Charts/chart-presets';
import { abbreviateNumber } from '@src/utils/data';
import { Channel4, Channel8 } from '@src/utils/colors';
import { ChartTooltip, PayloadItem } from '@Components/Charts/ChartTooltip/ChartTooltip';

const CHART_MARGIN = { top: 20, right: 20, bottom: 20, left: 20 };

interface AreaAndBarChartProps {
  areaDataKey: string;
  barDataKey: string;
  data: Record<string, unknown>[];
  xAxisDataKey: string;
  barYAxisDataKey: string;
  barYAxisLabel: string;
  areaYAxisDataKey: string;
  areaYAxisLabel: string;
  barChartColor?: string;
  areaChartLineColor?: string;
  areaChartFillColor?: string;
}

interface AreaAndBarChartLegendProps {
  barLabel: string;
  barColor: string;
  areaLabel: string;
  areaColor: string;
}

const AreaAndBarChartLegend = ({
  barLabel,
  barColor,
  areaLabel,
  areaColor
}: AreaAndBarChartLegendProps) => (
  <div className='area-and-bar-chart-legend'>
    <div className='legend-item'>
      <span className='legend-swatch square' style={{ backgroundColor: barColor }} />
      {humanize(barLabel)}
    </div>
    <div className='legend-item'>
      <span className='legend-swatch line' style={{ backgroundColor: areaColor }} />
      {humanize(areaLabel)}
    </div>
  </div>
);

interface AreaAndBarChartTooltipProps {
  active?: boolean;
  payload?: { dataKey: string; value: number }[];
  label?: string;
  barDataKey: string;
  barChartColor: string;
  areaChartLineColor: string;
}

const AreaAndBarChartTooltip = ({
  active,
  payload,
  label,
  barDataKey,
  barChartColor,
  areaChartLineColor
}: AreaAndBarChartTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;
  const items: PayloadItem[] = payload.map(item => ({
    label: humanize(item.dataKey),
    value: abbreviateNumber(Number(item.value)),
    color: item.dataKey === barDataKey ? barChartColor : areaChartLineColor,
    shape: item.dataKey === barDataKey ? 'square' : 'line'
  }));
  return <ChartTooltip headerLabel={label ?? ''} items={items} />;
};

export const AreaAndBarChart = ({
  areaDataKey,
  barDataKey,
  data,
  xAxisDataKey,
  barYAxisDataKey,
  barYAxisLabel,
  areaYAxisDataKey,
  areaYAxisLabel,
  barChartColor = Channel4,
  areaChartLineColor = Channel8,
  areaChartFillColor = Channel8
}: AreaAndBarChartProps) => (
  <div className='area-and-bar-chart'>
    <AreaAndBarChartLegend
      barLabel={barDataKey}
      barColor={barChartColor}
      areaLabel={areaDataKey}
      areaColor={areaChartLineColor}
    />
    <div className='area-and-bar-chart-plot'>
      <AutoSizer
        renderProp={({ width = 0, height = 0 }) => (
          <ComposedChart data={data} width={width} height={height} margin={CHART_MARGIN}>
            <ChartGrid vertical={false} />
            <ChartXAxis dataKey={xAxisDataKey} />
            <ChartYAxis
              dataKey={barYAxisDataKey}
              domain={[0, 'auto']}
              label={{ angle: -90, position: 'left', value: barYAxisLabel }}
              orientation='left'
              tickFormatter={tick => String(abbreviateNumber(Number(tick)))}
              type='number'
              yAxisId='0'
            />
            <ChartYAxis
              dataKey={areaYAxisDataKey}
              domain={[0, 'auto']}
              label={{ angle: -90, position: 'right', value: areaYAxisLabel }}
              orientation='right'
              tickFormatter={tick => String(abbreviateNumber(Number(tick)))}
              type='number'
              yAxisId='1'
            />
            <Tooltip
              isAnimationActive={false}
              content={
                <AreaAndBarChartTooltip
                  barDataKey={barDataKey}
                  barChartColor={barChartColor}
                  areaChartLineColor={areaChartLineColor}
                />
              }
            />
            <Bar
              barSize={20}
              dataKey={barDataKey}
              fill={barChartColor}
              yAxisId='0'
              isAnimationActive={false}
            />
            <Area
              yAxisId='1'
              stroke={areaChartLineColor}
              dataKey={areaDataKey}
              fill={areaChartFillColor}
              fillOpacity={0.15}
              type='monotone'
              isAnimationActive={false}
            />
          </ComposedChart>
        )}
      />
    </div>
  </div>
);
